import { BreakpointState } from '@angular/cdk/layout';
import { Component, HostBinding, Input } from '@angular/core';
import { IdtComponent } from 'shared/models/idt-components/idt-component.models';
import { ResponsiveSetting } from 'shared/responsive-settings/responsive-settings.interface';
import { AmandaOptions } from 'shared/services/assets-shared.service';

import { HeaderIcon, OpenModeType, SliceAccordionSettings } from 'extensions/core/slice-accordion/front/settings/slice-accordion-settings.model';
import { SliceBaseHostStyles, SliceBaseRenderComponent } from 'extensions/core/slice-base/render/slice-base-render.component';


@Component({
  selector: 'slice-accordion-render',
  templateUrl: './slice-accordion-render.component.html',
  styleUrls: ['./slice-accordion-render.component.scss']
})
export class SliceAccordionRenderComponent extends SliceBaseRenderComponent {

  @Input() public slice!: IdtComponent<SliceAccordionSettings>;

  public currentElement?: number;
  public elementsRange!: number[];
  public loadedElements!: Record<number, boolean>;
  public expandedElements!: Record<number, boolean>;

  @HostBinding('style.--header-icon-position')
  public get headerIconPosition(): string {
    return this.slice.settings?.general.headerIcon.position === 'left' ? 'row-reverse' : 'row';
  }

  @HostBinding('style.--header-icon-justify-content')
  public get headerIconJustifyContent(): string {
    return this.slice.settings?.general.headerIcon.position === 'left' ? 'start' : 'space-between';
  }

  @HostBinding('style.--header-icon-width')
  public get headerIconWidth(): string {
    return this.slice.settings?.general.headerIcon.width ?? '20px';
  }

  @HostBinding('style.--header-icon-height')
  public get headerIconHeight(): string {
    return this.slice.settings?.general.headerIcon.height ?? '20px';
  }

  @HostBinding('class.accordion-theme-solid')
  public get accordionThemeSolid(): boolean {
    return this.slice.settings?.styles.accordionAppearance.theme === 'solid';
  }

  @HostBinding('class.accordion-theme-default')
  public get accordionThemeDefault(): boolean {
    return this.slice.settings?.styles.accordionAppearance.theme === 'default';
  }

  public get allowExpandMultipleHeader(): boolean {
    return !!this.slice.settings?.general.allowExpandMultipleHeader;
  }

  public get headerTypeHasIcon(): boolean {
    return this.slice.settings?.general.headerType === 'icon-and-text';
  }

  public getIconSrc(icon: HeaderIcon, isActiveHeader: boolean): string | undefined {
    const iconUrl = isActiveHeader ? icon.activeIcon.image : icon.icon.image;
    const assetsUrl = this.renderConfigurationService.config.assetsUrl;
    const akamaiOptions: AmandaOptions = { isWidthParamEnabled: false };

    return this.assetsSharedService.getAssetUrl(iconUrl!, assetsUrl, akamaiOptions);
  }

  public getHeaderIconSrc(iconUrl: string): string | undefined {
    const assetsUrl = this.renderConfigurationService.config.assetsUrl;
    const akamaiOptions: AmandaOptions = { isWidthParamEnabled: false };

    return this.assetsSharedService.getAssetUrl(iconUrl, assetsUrl, akamaiOptions);
  }

  public goToElement(elementIndex: number, eventType: OpenModeType): void {
    if (eventType === this.slice.settings?.general.openMode) {
      if (this.expandedElements[elementIndex]) {
        this.currentElement = undefined;
      } else {
        this.loadedElements[elementIndex] = true;
        this.currentElement = elementIndex;
      }

      if (this.allowExpandMultipleHeader) {
        this.expandedElements[elementIndex] = !this.expandedElements[elementIndex];
      } else {
        this.expandedElements = Object.fromEntries(this.elementsRange.map(
          (element) => [element, element === elementIndex ? !this.expandedElements[element] : false]
        ));
      }
    }
  }

  public trackByIndex(index: number): number {
    return index;
  }

  protected onMediaChange(breakpointState: BreakpointState): void {
    super.onMediaChange(breakpointState);

    this.initAccordion();
  }

  protected onSliceInit(): void { /* empty */ }

  protected setDefaultHostStyles(): void {
    this.setHostStyles({
      backgroundColor: this.slice.settings!.styles.bgColor || '',
      marginTop: this.slice.settings!.styles.marginTop || '',
      marginRight: this.slice.settings!.styles.marginRight || '',
      marginLeft: this.slice.settings!.styles.marginLeft || '',
      marginBottom: this.slice.settings!.styles.marginBottom || '',
      paddingTop: this.slice.settings!.styles.paddingTop,
      paddingRight: this.slice.settings!.styles.paddingRight,
      paddingLeft: this.slice.settings!.styles.paddingLeft,
      paddingBottom: this.slice.settings!.styles.paddingBottom
    });
  }

  protected setResponsiveHostStyles(
    breakpointStyles: ResponsiveSetting,
    defaultBpSettings: Partial<ResponsiveSetting>
  ): void {
    this.setHostStyles({
      backgroundColor: this.slice.settings!.styles.bgColor || '',
      marginTop: breakpointStyles.marginTop || defaultBpSettings.marginTop || '',
      marginRight: breakpointStyles.marginRight || defaultBpSettings.marginRight || '',
      marginLeft: breakpointStyles.marginLeft || defaultBpSettings.marginLeft || '',
      marginBottom: breakpointStyles.marginBottom || defaultBpSettings.marginBottom || '',
      paddingTop: breakpointStyles.paddingTop || defaultBpSettings.paddingTop || '',
      paddingRight: breakpointStyles.paddingRight || defaultBpSettings.paddingRight || '',
      paddingLeft: breakpointStyles.paddingLeft || defaultBpSettings.paddingLeft || '',
      paddingBottom: breakpointStyles.paddingBottom || defaultBpSettings.paddingBottom || ''
    });
  }

  private setHostStyles(styles: SliceBaseHostStyles): void {
    this.backgroundColor = styles.backgroundColor;
    this.marginTop = styles.marginTop;
    this.marginRight = styles.marginRight;
    this.marginLeft = styles.marginLeft;
    this.marginBottom = styles.marginBottom;
    this.paddingTop = styles.paddingTop;
    this.paddingRight = styles.paddingRight;
    this.paddingLeft = styles.paddingLeft;
    this.paddingBottom = styles.paddingBottom;
  }

  private initAccordion(): void {
    this.elementsRange = Object.keys(this.slice.children)
      .filter((childKey) => !this.slots[+childKey - 1].hidden && this.hasContent(this.slice.children[childKey][0]))
      .map((childKey) => +childKey - 1);

    this.loadedElements = Object.fromEntries(this.elementsRange.map((element) => [element, false]));
    this.expandedElements = { ...this.loadedElements };
  }

}

